import * as React from 'react'

import { Box, Section } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import TextInput from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { useEffect } from 'modules/core/hooks/useEffect'
import { Return } from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EffectiveAtDisclaimerModal from 'modules/attendance/components/SettingItemPage/TimeTracking/EffectiveAtDisclaimerModal'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import AttendancePolicyForm from 'modules/attendance/lib/forms/AttendancePolicyForm'
import TimeTrackingSettingForm from 'modules/attendance/lib/forms/TimeTrackingSettingForm'
import attendanceSettings from 'modules/attendance/store/collections/settings'
import type AttendancePolicy from 'modules/attendance/store/models/AttendancePolicy'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import Timesheet from '../Timesheet'
import useUpdateAttendancePolicy from '../hooks/useUpdateAttendancePolicy'
import Toggles from './Toggles'

interface Props {
  attendancePolicy: AttendancePolicy
  attendanceSetting: AttendanceSetting
}

const createPolicyForm = (policy: AttendancePolicy) => new AttendancePolicyForm(policy.toJS())

const PolicyDetails = ({ attendancePolicy, attendanceSetting }: Props) => {
  const defaultPolicyForm = attendancePolicy
    ? createPolicyForm(attendancePolicy)
    : new AttendancePolicyForm({})
  const [policyForm, setPolicyForm] = React.useState(defaultPolicyForm)
  const settingForm = TimeTrackingSettingForm(attendanceSetting)
  const [openEffectiveAtDisclaimerModal, setOpenEffectiveAtDisclaimerModal] = React.useState(false)
  const { updatePolicy } = useUpdateAttendancePolicy(policyForm, attendancePolicy)

  const updateSetting = async (value?: { successFlash: boolean }): Promise<Return<void>> => {
    const successFlash = value?.successFlash ?? true

    await settingForm.submitForm().then((handled) => {
      if (handled.success && successFlash) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
    })

    await attendanceSettings.fetch({
      data: { employees_policy_ids: [attendanceSettingPolicyId] },
    })

    return Promise.resolve({ success: successFlash, value: undefined })
  }

  const settingFormEffectiveAt = settingForm.values.effectiveAt

  const {
    policyId: attendanceSettingPolicyId,
    attributes: { effective_at: attendanceSettingEffectiveAt },
  } = attendanceSetting

  const settings = attendanceSettings.orderByEffectiveAt(attendanceSettingPolicyId)
  const firstSetting = settings[0]
  const maxDate = attendanceSetting.equals(firstSetting) ? moment().endOf('day') : null

  useEffect(() => {
    setPolicyForm(createPolicyForm(attendancePolicy))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendancePolicy.attributes.id, attendanceSetting.id])

  const performAction = async () => {
    setOpenEffectiveAtDisclaimerModal(false)

    if (policyForm.isDirty) await updatePolicy()

    if (settingForm.dirty) await updateSetting({ successFlash: true })

    await attendanceSettings.fetch({
      data: { employees_policy_ids: [attendanceSettingPolicyId] },
    })
  }

  const onSubmit = () => {
    if (settingFormEffectiveAt === attendanceSettingEffectiveAt) {
      performAction()
    }
    setOpenEffectiveAtDisclaimerModal(true)
  }

  const { featureAvailability: timeTrackingGeofencingAlertsAvailability } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )

  return (
    <Box flexGrow>
      <Section.Form
        illustration="multiple-buildings"
        title={i18n.t('time_tracking.settings.basics.title')}
        description={i18n.t('time_tracking.settings.system.description')}
        onSubmit={onSubmit}
        isDirty={policyForm.isDirty || settingForm.dirty}
        submitLabel={i18n.t('common.submit')}
        extra={{ 'data-intercom-target': 'attendance-policy-details-form' }}
      >
        <TextInput
          label={i18n.t('time_tracking.settings.form.policy_name')}
          field={policyForm.get('name')}
        />
        <TextArea
          label={i18n.t('time_tracking.settings.form.policy_description')}
          field={policyForm.get('description')}
        />
        <DayPicker
          label={i18n.t('time_tracking.settings.versions.effective_date')}
          value={settingForm.values.effectiveAt ?? dateToString(moment(), 'ui')}
          valueFormat="backend"
          onChange={(newDate: string) => settingForm.setFieldValue('effectiveAt', newDate)}
          maxDate={maxDate}
        />
      </Section.Form>
      <Section
        illustration="clock-app"
        title={i18n.t('time_tracking.settings.system.title')}
        description={
          timeTrackingGeofencingAlertsAvailability
            ? i18n.t('time_tracking.settings.form.toggle.geolocation.time_tracking_system')
            : i18n.t('time_tracking.settings.system.description')
        }
        extra={{ 'data-intercom-target': 'attendance-policy-details-toggles' }}
      >
        <Toggles
          updatePolicy={updateSetting}
          policyForm={settingForm}
          attendanceSetting={attendanceSetting}
        />
      </Section>

      {openEffectiveAtDisclaimerModal && (
        <EffectiveAtDisclaimerModal
          onConfirm={performAction}
          onClose={() => setOpenEffectiveAtDisclaimerModal(false)}
        />
      )}
      <Timesheet attendanceSetting={attendanceSetting} />
    </Box>
  )
}

export default observer(PolicyDetails)
